import { Badge, capitalize, IconButton, Skeleton, Stack } from '@mui/material';
import { getSelectorAttribute, headerSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { isNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import { useAccount } from '../../../../hooks/requests/use-account';
import { useJobs } from '../../../../hooks/requests/use-jobs';
import { useSubsidiaries } from '../../../../hooks/requests/use-subsidiaries';
import { sliceLongString } from '../../../../utils/string';
import { AiCopilotButton } from '../../../ai-copilot/AiCopilotButton';
import { mapJobToNotification } from '../../feedback/VyNotificationCard/vyNotificationCardUtils';
import { VyNotificationCenter } from '../../feedback/VyNotificationCenter/VyNotificationCenter';
import { ImportContractDialog } from './vy-search-bar-components/ImportContractDialog';
import { VyAccountDropDown } from './vy-search-bar-components/VyAccountDropDown';

export const VySearchBar: React.FC = () => {
  const { data, isLoading: isLoadingAccount } = useAccount();
  const { data: subsidiariesData, isLoading: isLoadingSubsidiaries } = useSubsidiaries();
  const { data: jobs } = useJobs();
  const subsidiaries = subsidiariesData?.subsidiaries ?? [];
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const MAX_LENGTH = 17;
  const formattedAccountName = capitalize(sliceLongString(data?.account.name ?? 'account', MAX_LENGTH));

  const notifications = jobs?.map(mapJobToNotification) ?? [];
  const unreadNotifications = notifications.filter((notification) => isNullOrUndefined(notification.readAt));
  const unreadCount = unreadNotifications.length;

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: "center",
        justifyContent: "flex-end",
        px: 5,
        py: 0,
        bgcolor: "white",
        borderBottom: 1,
        borderColor: "grey.300",
        width: "100%",
        height: "75px"
      }}>
      {isLoadingAccount || isLoadingSubsidiaries ? (
        <Skeleton
          variant="rectangular"
          sx={{
            minWidth: '200px',
            height: '35px',
            borderRadius: '4px',
            padding: '6px 8px',
          }}
        />
      ) : (
        <>
          <AiCopilotButton />

          <IconButton
            {...getSelectorAttribute(headerSelectors.notifications.notificationButton)}
            onClick={handleNotificationClick}
            sx={{
              width: 40,
              height: 40,
              '&:hover': { backgroundColor: '#5o83ff' },
            }}
          >
            <Badge
              badgeContent={unreadCount}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#5083ff',
                  color: 'white',
                  fontSize: '11px',
                  height: '18px',
                  minWidth: '18px',
                  borderRadius: '9px',
                },
              }}
            >
              <VyIcon icon="NotificationBell" color="#020f20" />
            </Badge>
          </IconButton>

          <VyAccountDropDown
            {...getSelectorAttribute(headerSelectors.accountSettings.accountDropdownButton)}
            accountName={formattedAccountName}
            subsidiaries={subsidiaries}
            logo={data?.account.logoUrl}
          />
        </>
      )}
      <VyNotificationCenter
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
        notifications={notifications}
      />
      <ImportContractDialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />
    </Stack>
  );
};
